"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { HiOutlineXMark } from "react-icons/hi2";
import { Button, Text } from "@/components";
import {
  IconButton,
  Modal,
  ModalBody,
  ModalFooter,
  TouchAwareTooltip,
} from "@/components/ui";
import { getSelectedItems } from "@/lib/selection";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";

interface AdminSelectedItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  selectedIds: number[];
  selectedNames: Record<number, string>;
  fallbackName: (id: number) => string;
  onRemove: (id: number) => void;
  onUnselectAll: () => void;
  isUnselectLoading?: boolean;
  actions: ReactNode;
  testId?: string;
}

export function AdminSelectedItemsModal({
  isOpen,
  onClose,
  title,
  selectedIds,
  selectedNames,
  fallbackName,
  onRemove,
  onUnselectAll,
  isUnselectLoading = false,
  actions,
  testId = "admin-selected-modal",
}: AdminSelectedItemsModalProps) {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const items = getSelectedItems(selectedIds, selectedNames, fallbackName);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="md"
      contentProps={{ "data-testid": testId }}
    >
      <ModalBody px={{ base: 4, md: 6 }} py={0} minH={0}>
        <Flex direction="column" py={2}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;

            return (
              <Flex
                key={item.id}
                align="center"
                gap={3}
                py={3}
                borderBottomWidth={isLast ? 0 : "1px"}
                borderColor={theme.borderGlass.subtle}
                data-testid={`${testId}-row-${item.id}`}
              >
                <Box minW={0} flex={1}>
                  <Text
                    fontSize="sm"
                    fontWeight="600"
                    color={theme.text.primary}
                    noOfLines={2}
                    title={item.name}
                  >
                    {item.name}
                  </Text>
                </Box>
                <TouchAwareTooltip
                  label={`Unselect ${item.name}`}
                  placement="top"
                  openDelay={300}
                >
                  <Box display="inline-flex">
                    <IconButton
                      aria-label={`Unselect ${item.name}`}
                      icon={<HiOutlineXMark size={18} />}
                      size="sm"
                      variant="ghost"
                      color={theme.text.tertiary}
                      minW="32px"
                      h="32px"
                      onClick={() => onRemove(item.id)}
                      sx={{
                        "@media (hover: hover)": {
                          "&:hover": {
                            color: theme.text.primary,
                            bg: interactive.neutral.bg,
                          },
                        },
                      }}
                    />
                  </Box>
                </TouchAwareTooltip>
              </Flex>
            );
          })}
        </Flex>
      </ModalBody>
      <ModalFooter
        flexShrink={0}
        px={{ base: 4, md: 6 }}
        py={4}
        gap={3}
        borderTopWidth="1px"
        borderTopColor={theme.borderGlass.default}
        flexDirection={{ base: "column", sm: "row" }}
        justifyContent={{ base: "stretch", sm: "flex-end" }}
      >
        <Button
          variant="outline"
          h="44px"
          w={{ base: "100%", sm: "auto" }}
          onClick={onUnselectAll}
          isLoading={isUnselectLoading}
          loadingText="Loading..."
          isDisabled={isUnselectLoading}
          data-testid={`${testId}-unselect-all`}
        >
          Unselect all
        </Button>
        <Box w={{ base: "100%", sm: "auto" }} flexShrink={0}>
          {actions}
        </Box>
      </ModalFooter>
    </Modal>
  );
}
